import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '../api/profile.api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../../../shared/components/Toast/ToastProvider';

// Query-key factory for the profile feature.
export const profileKeys = {
  all: ['profile'],
};

// useProfile — fetch the current user's profile from the API.
// Only runs when authenticated. Falls back to the locally-stored user
// (from AuthContext) as placeholder data so the UI can render instantly.
export function useProfile() {
  const { isAuthenticated, user } = useAuth();
  return useQuery({
    queryKey: profileKeys.all,
    queryFn: () => profileApi.get(),
    enabled: isAuthenticated,
    placeholderData: user ?? undefined,
    staleTime: 60_000,
  });
}

// useUpdateProfile — save name/email changes.
// On success: invalidate the cached profile, merge the patch into the
// stored auth user, and toast. Field errors are surfaced via the thrown
// ApiError (err.fieldErrors) so the caller can map them onto inputs.
export function useUpdateProfile() {
  const qc = useQueryClient();
  const toast = useToast();
  const { updateUser } = useAuth();
  return useMutation({
    mutationFn: (patch) => profileApi.update(patch),
    onSuccess: (_data, patch) => {
      qc.invalidateQueries({ queryKey: profileKeys.all });
      updateUser(patch);
      toast.success('Profile updated');
    },
    onError: (err) => {
      toast.error(err?.message || 'Could not update profile. Please try again.');
    },
  });
}

// useDeactivateAccount — deactivate the account, then log the user out.
export function useDeactivateAccount() {
  const toast = useToast();
  const { logout } = useAuth();
  return useMutation({
    mutationFn: () => profileApi.deactivate(),
    onSuccess: () => {
      toast.success('Your account has been deactivated');
      logout();
    },
    onError: (err) => {
      toast.error(err?.message || 'Could not deactivate account. Please try again.');
    },
  });
}
